import React, { Suspense, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import AnimatedResident from '../AnimatedResident';
import WorldLabel, { nameProps } from '../WorldLabel';
import SafeMount from '../SafeMount';
import { useWorldStore } from '../../state/worldStore';
import { useCityStore } from '../../city/cityStore';
import { stateAt } from '../../city/cityMotion';
import { aboardAvatars } from '../../city/heliTour';
import { onDeck } from '../../city/stagePlan';
import { talkMove, chatTalkMove, habitMove, emoteMove } from '../../city/bodyLanguage';
import { groundY } from '../../city/cityGround';
import { useCutscene, cutsceneBridge } from '../../city/cutscene';
import CarModel from '../CarModel';
import { assetUrl } from '../../services/assets';

// One colour per artist, so their car is recognisably theirs from across the street.
const CAR_PAINTS = ['#b3261e', '#1f4e8c', '#e0b42b', '#2f6b3a', '#6b3fa0', '#d9d9d9', '#1b1b1f', '#c2185b'];
import { bodyAt, readyBodies, readyAt } from '../../city/bodies';
import { serverNow, cityEngage, citySocketId, busyLine } from '../../services/worldSocket';
import { requestLoad, releaseLoad, bodyGone, labelStyle, PREFETCH_RADIUS, LOAD_RADIUS, UNLOAD_RADIUS } from '../Npc';
import { maybeBark } from '../../services/barkEngine';
import { ktx2Extend } from '../../utils/ktx2';
import { bodyScene, useSceneChat } from '../SceneChat';

// CityResident — an artist living in the city (the Living City, 2026-10-23).
//
// Khari: *"Their avatar would be driven by their decisions and their actions in
// the city."* Nothing here decides anything. The artist's day arrives as a
// timeline from the server (their own day, their own choices); this body only
// stands where that timeline says, walks where it walks, turns to whoever they
// are talking to, and answers a tap the way the rule says:
//
//   FREE   → they stop for you and you can talk, until their next story.
//   STORY  → *"the users cannot disturb them"* — a tap says what they are busy
//            with, and nothing else happens.
//
// The body follows its timeline position at walking pace rather than snapping to
// it, so a small disagreement (a clock correction, a path worked out a moment
// late) is a step or two, never a teleport.

const hash = (n) => { const x = Math.sin(n * 12.9898) * 43758.5453; return x - Math.floor(x); };
const STRIDE = 0.9;            // world units per second the walk clip covers at rate 1 (≈0.75-tall body)
const ATTENTION = 2.4;         // a free artist turns to someone this close
const MID = 9;

function ease(cur, want, k) {
  let d = want - cur;
  d = Math.atan2(Math.sin(d), Math.cos(d));
  return cur + d * k;
}

export default function CityResident({ resident, body }) {
  // A cutscene is a film: no nameplates floating over it.
  const inCut = useCutscene((s) => s.phase === 'cut');
  const avatarId = resident.avatarId;
  const name = resident.name || body?.name || 'Artist';
  const first = name.split(' ')[0] || name;
  const height = body?.height || 0.75;
  const openChat = useWorldStore((s) => s.openChat);
  const camera = useThree((s) => s.camera);
  const gl = useThree((s) => s.gl);
  const ktx2 = useMemo(() => ktx2Extend(gl), [gl]);

  const group = useRef();
  const pos = useRef({ x: 0, z: 0, init: false, heading: 0, speed: 0 });
  const [present, setPresent] = useState(false);
  const presentRef = useRef(false);
  const [walking, setWalking] = useState(false);
  // In a car, during a City Story's drive (city/cityDrive): the car is all that
  // shows (Khari, 2026-10-24: "we don't need to show the character in the car
  // while driving. just show the vehicle" — seated, their head came through the
  // roof). The body stays mounted, hidden, so stepping out is instant.
  const [driving, setDriving] = useState(false);
  const carScale = useWorldStore((s) => s.carScale);
  const [rate, setRate] = useState(1);
  const [speaking, setSpeaking] = useState(false);
  // The library move their set calls for right now (city/stagePlan via stateAt `move`).
  const [stageMove, setStageMove] = useState(null);
  const stageMoveRef = useRef(null);
  // The move is their talking (city/bodyLanguage): it plays through a voice call too.
  const [moveTalks, setMoveTalks] = useState(false);
  const stageTalksRef = useRef(false);
  // Their stage walk: loaded while they are on the stage, walked with when the plan says.
  const [onStageNow, setOnStageNow] = useState(false);
  const [stageGait, setStageGait] = useState(false);
  const [tier, setTier] = useState('far');
  const [state, setState] = useState({ mode: null, activity: null, onStage: false });
  const [note, setNote] = useState(null);   // a short line after a tap that could not stop them
  const tierRef = useRef('far');
  const modeRef = useRef(null);

  // Streaming, in the same queue as every other resident (one body at a time).
  const slug = `city-${avatarId}`;
  const [bodyOn, setBodyOn] = useState(false);
  const bodyOnRef = useRef(false);
  const queuedRef = useRef(false);
  const slotRef = useRef(false);
  const prefetched = useRef(false);

  const seed = avatarId;
  const phaseOffset = hash(seed + 1);
  const idleRate = 0.9 + hash(seed + 2) * 0.2;

  useFrame((_, dt) => {
    const g = group.current;
    if (!g) return;
    const t = serverNow();
    const cs = useCityStore.getState();
    const r = cs.byId[avatarId] || resident;
    let st = stateAt(r, t, cs.convs);
    const hold = cs.holds[avatarId];
    const held = !!(hold && t >= hold.since && t < hold.until);
    if (held && st) {
      const frozen = stateAt(r, hold.since, cs.convs) || st;
      st = { ...frozen, moving: false, speed: 0, mode: 'held' };
    }

    // INSIDE A ROOM for a story scene (st.inside): off the street until they come
    // back out the door, where they reappear (init reset). RoomStory draws them.
    // …or AT HOME, drawn by their home (API city/cityHome `hm` 'heli': the heliport draws its pilots),
    // …or UP IN A HELICOPTER (API city/HeliRideService): seated in its cabin, drawn there (heli/HeliPassengers).
    if (!st || st.inside || st.seg?.hm === 'heli' || aboardAvatars().has(Number(avatarId))) {
      if (presentRef.current) { presentRef.current = false; setPresent(false); }
      g.visible = false;
      pos.current.init = false;
      return;
    }
    if (!presentRef.current) { presentRef.current = true; setPresent(true); }
    g.visible = true;

    // ── Follow the timeline, at walking pace ─────────────────────────────────
    const P = pos.current;
    const dx = st.x - P.x; const dz = st.z - P.z;
    const dist = Math.hypot(dx, dz);
    const step = Math.max(0.0001, dt);
    // A CUT in a replayed trip (city/replay): the film jumps ahead, so does the body.
    const cut = !!st.seg?.cut && st.seg !== P.seg;
    P.seg = st.seg;
    // TALKING WITH YOU, THEY STAND STILL — where the body is, from the moment the
    // chat opens (2026-11-01, Khari: "if you initiate a conversation with an
    // avatar while they are walking, the chat scene will follow them"). The hold
    // above freezes their TIMELINE, but it arrives over the socket a moment later
    // and at wherever the timeline was then — so the body walked on, then back to
    // that point, and the chat scene's camera (which follows the body) went with it.
    const withMe = P.init && String(useWorldStore.getState().chatAvatar?.avatarId ?? '') === String(avatarId);
    const fresh = !withMe && (!P.init || dist > 30 || cut);
    if (fresh) { P.x = st.x; P.z = st.z; P.init = true; P.speed = 0; if (cut) P.heading = st.heading; }
    else if (withMe) P.speed = 0;
    else if (dist > 0.0005) {
      const max = (st.moving ? Math.max(st.speed * 1.2, 0.9) : 1.1) * step;
      const k = Math.min(1, max / dist);
      P.x += dx * k; P.z += dz * k;
      P.speed += ((dist * k) / step - P.speed) * 0.25;
    } else P.speed *= 0.8;
    // Feet on the ground where the BODY is (it trails the timeline a little), so a
    // kerb is stepped up where it is crossed; on the stage, on the stage.
    // ⚠ THE WHOLE DECK, not just its middle (2026-11-04): a set crosses the stage
    // between its marks (city/stagePlan), and a body more than 0.6 from its mark
    // used to drop to the lot below it, through the floor.
    const stagePl = st.spot === 'stage' ? (cs.snap?.places || []).find((p) => p.slug === st.place)?.stage : null;
    const footY = st.spot === 'stage' && (dist < 0.6 || onDeck(stagePl, P.x, P.z)) ? st.y : groundY(P.x, P.z);
    P.y = fresh || P.y == null ? footY : P.y + (footY - P.y) * Math.min(1, step * 10);
    g.position.set(P.x, P.y, P.z);

    // ── Which way they face ─────────────────────────────────────────────────
    const camDx = camera.position.x - P.x; const camDz = camera.position.z - P.z;
    const camDist = Math.hypot(camDx, camDz);
    let want = st.heading;
    if (withMe) want = Math.atan2(camDx, camDz);   // to you (the chat scene's camera comes in from your side)
    else if (P.speed > 0.12 && dist > 0.002) want = Math.atan2(dx, dz);
    else if (held) {
      // To the visitor they stopped for: this viewer, or that visitor's orb.
      if (hold.by && hold.by === citySocketId()) want = Math.atan2(camDx, camDz);
      else {
        const peer = (useWorldStore.getState().presences || []).find((p) => p.id === hold.by && Array.isArray(p.pos));
        if (peer) want = Math.atan2(peer.pos[0] - P.x, peer.pos[2] - P.z);
      }
    } else if (st.mode === 'free' && !st.convId && camDist < ATTENTION) want = Math.atan2(camDx, camDz);
    P.heading = ease(P.heading, want, Math.min(1, step * 6));
    g.rotation.y = P.heading;
    bodyAt.set(Number(avatarId), { x: P.x, y: P.y, z: P.z, heading: P.heading, at: performance.now() });

    // ── Walk or stand ────────────────────────────────────────────────────────
    const isWalking = P.speed > 0.12;
    if (isWalking !== walking) setWalking(isWalking);
    // On the stage: the set's move while they stand on a mark, their walk while
    // they cross to the next one (an activity would play over the walk).
    // Off it (city/bodyLanguage): their hands while they talk, their own cue on a
    // line, and now and then one of the habits they chose this morning.
    let mv = st.move && !isWalking && !driving ? st.move : null;
    let talks = false;
    // A move they chose to do on request (city/moveBook) comes first, wherever they stand.
    const asked = !isWalking && !driving ? emoteMove(cs.emotes, avatarId, t) : null;
    if (asked) { mv = asked; talks = true; }
    else if (!mv && !isWalking && !driving && st.spot !== 'stage' && !st.seg?.cf && !st.seg?.ho) {
      const e = Number.isFinite(r.e) ? r.e : 50;
      if (withMe) {
        // Their reply to you being spoken, typed (SceneChat) or by voice: their hands.
        const toYou = useWorldStore.getState().residentTalking || useSceneChat.getState().speaking === Number(avatarId);
        mv = toYou ? chatTalkMove(avatarId, t, e) : null; talks = !!mv;
      } else if (st.convId) { mv = talkMove(cs.convs[st.convId], avatarId, t, e); talks = !!mv; }
      // A scene of a City Story out here: their hands on their lines, listening on the other's (no habits mid-scene).
      else if (st.story != null && cs.stories?.[st.story]?.lines?.length) { mv = talkMove({ lines: cs.stories[st.story].lines }, avatarId, t, e); talks = !!mv; }
      else if (!held && (st.mode === 'free' || st.mode === 'story')) mv = habitMove(avatarId, r.h, t, e);
    }
    if (talks !== stageTalksRef.current) { stageTalksRef.current = talks; setMoveTalks(talks); }
    if (mv !== stageMoveRef.current) { stageMoveRef.current = mv; setStageMove(mv); }
    const upThere = !!(st.show && st.spot === 'stage');
    if (upThere !== onStageNow) setOnStageNow(upThere);
    const sg = upThere && st.gait !== 'walk';   // between steps: keep the last choice
    if (st.stageWalk && sg !== stageGait) setStageGait(sg);
    if (!!st.driving !== driving) setDriving(!!st.driving);
    const wantRate = isWalking ? Math.max(0.6, Math.min(1.9, Math.round((P.speed / (STRIDE * (height / 0.75))) * 4) / 4)) : 1;
    if (wantRate !== rate) setRate(wantRate);

    // Speaking a line of a conversation right now?
    const sp = (cs.speaking[avatarId] || 0) > t;
    if (sp !== speaking) setSpeaking(sp);

    // ── How close the viewer is, and what the label says ────────────────────
    const tr = camDist < ATTENTION ? 'near' : camDist < MID ? 'mid' : 'far';
    if (tr !== tierRef.current) { tierRef.current = tr; setTier(tr); }
    const m = held ? 'held' : (st.convId ? 'talking' : st.mode);
    // Playing a set: the stage's own screen says who they are (city/stageScreen).
    const onStage = !!(st.show && st.spot === 'stage');
    if (m !== modeRef.current || st.activity !== state.activity || onStage !== state.onStage) { modeRef.current = m; setState({ mode: m, activity: st.activity, onStage }); }

    // ── Streaming the body ───────────────────────────────────────────────────
    // ⚠ BY WHERE THE VISITOR STANDS, not where the camera is (2026-10-29, the
    // plaza stuttering on a phone): in scene view the camera cuts between shots
    // tens of units apart, and with the phone's short radii every cut loaded or
    // freed bodies (a GLB parse each time, on the main thread). The cutscene's
    // saved view (`at`) is where you really are.
    const me = (cutsceneBridge.active && cutsceneBridge.at) || camera.position;
    const standDist = Math.hypot(me.x - P.x, me.z - P.z);
    if (!prefetched.current && standDist < PREFETCH_RADIUS && body) {
      prefetched.current = true;
      for (const url of [body.rigged, body.walk, body.idle].filter(Boolean)) {
        try { useGLTF.preload(url, true, true, ktx2); } catch { /* best effort */ }
      }
    }
    const wantBody = !!body?.rigged && (bodyOnRef.current ? standDist < UNLOAD_RADIUS : standDist < LOAD_RADIUS);
    if (wantBody) {
      if (!bodyOnRef.current) {
        queuedRef.current = true;
        requestLoad(slug, standDist, () => { slotRef.current = true; bodyOnRef.current = true; queuedRef.current = false; setBodyOn(true); });
      }
    } else if (bodyOnRef.current || queuedRef.current) {
      releaseLoad(slug, slotRef.current);
      bodyGone(slug);
      slotRef.current = false; queuedRef.current = false;
      if (bodyOnRef.current) { bodyOnRef.current = false; setBodyOn(false); }
    }
  });

  useEffect(() => () => { releaseLoad(slug, slotRef.current); bodyGone(slug); slotRef.current = false; bodyAt.delete(Number(avatarId)); readyBodies.delete(Number(avatarId)); readyAt.delete(Number(avatarId)); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps
  // Their real body is up (and stays counted until it is unloaded: readyBodies).
  useEffect(() => { if (!bodyOn) { readyBodies.delete(Number(avatarId)); readyAt.delete(Number(avatarId)); } }, [bodyOn, avatarId]);
  const onReady = useCallback(() => {
    readyBodies.add(Number(avatarId));
    if (!readyAt.has(Number(avatarId))) readyAt.set(Number(avatarId), performance.now());
    if (!slotRef.current) return;
    slotRef.current = false;
    releaseLoad(slug, true);
  }, [slug, avatarId]);

  // A tap: free → they stop for you and the chat opens; busy → it says so.
  const onSelect = useCallback(async () => {
    const mode = modeRef.current;
    if (mode !== 'free' && mode !== 'held') {
      setNote(busyLine(first, mode === 'talking' ? 'talking' : 'busy', state.activity));
      return;
    }
    const r = await cityEngage(avatarId);
    if (!r?.ok) { setNote(busyLine(first, r?.reason, r?.activity)); return; }
    // AS A SCENE (components/SceneChat): held for you, they turn to face you (the
    // heading block above), so the camera comes in from your side of them.
    openChat({ avatarId, name, city: { mustGoAt: r.mustGoAt || null, nextActivity: r.nextActivity || null }, scene: bodyScene(group.current, { avatarId, height }) });
  }, [avatarId, name, first, openChat, state.activity, height]);
  useEffect(() => {
    if (!note) return undefined;
    const tm = setTimeout(() => setNote(null), 3200);
    return () => clearTimeout(tm);
  }, [note]);

  const onHover = useCallback(() => {
    if (modeRef.current === 'free' && body?.slug) maybeBark(body.slug, Math.random() < 0.6 ? 'greeting_passerby' : 'idle_chatter');
  }, [body?.slug]);

  const inChat = useWorldStore((s) => String(s.chatAvatar?.avatarId ?? '') === String(avatarId));
  const free = state.mode === 'free';
  const showLabel = present && !inChat && (tier !== 'far' || !!note);
  const labelText = note
    || (tier === 'near' && free ? `💬 ${first} — tap to talk`
      : tier === 'near' && state.mode === 'talking' ? `${first} · in conversation`
        : tier === 'near' && state.mode === 'held' ? `${first} · talking with someone`
          : tier === 'near' && state.activity && state.mode === 'story' ? `${first} · ${state.activity}`
            : name);

  return (
    <group ref={group} visible={false}>
      {present && driving && (
        // Their car: the street's own Elise, in a colour that is theirs.
        <group scale={carScale}>
          <SafeMount label={`car for ${name}`} retryMs={20000}>
            <Suspense fallback={null}>
              <CarModel url={assetUrl('/models/cars/elise.glb')} length={3.8} rotY={Math.PI} tint={CAR_PAINTS[Number(avatarId) % CAR_PAINTS.length]} />
            </Suspense>
          </SafeMount>
        </group>
      )}
      {present && !bodyOn && !driving && (
        // A stand-in at their height until the body arrives (and when far away).
        <mesh position={[0, height * 0.5, 0]}>
          <capsuleGeometry args={[height * 0.13, height * 0.62, 4, 8]} />
          <meshStandardMaterial color="#2e2a3a" roughness={0.8} metalness={0} />
        </mesh>
      )}
      {present && bodyOn && body && (
        <group visible={!driving}>
          <SafeMount label={`city resident ${name}`} retryMs={30000} onRetry={() => useGLTF.clear([body.rigged, body.walk, body.idle].filter(Boolean))}>
            <Suspense fallback={null}>
              <AnimatedResident
                meshUrl={body.rigged}
                rigPose={body.pose || null}
                clipMap={body.clips || null}
                clipUrls={[body.walk, body.idle].filter(Boolean)}
                play={walking && !driving ? 'walk' : 'idle'}
                activity={stageMove}
                activityTalks={moveTalks}
                gaitUrl={onStageNow ? body.clips?.stage_walk || null : null}
                gait={onStageNow && stageGait}
                timeScale={walking ? rate : 1}
                avatarId={avatarId}
                position={[0, 0, 0]}
                rotationY={0}
                height={height}
                phaseOffset={phaseOffset}
                rate={walking ? 1 : idleRate}
                speaking={speaking}
                onReady={onReady}
                onSelect={onSelect}
                onHover={onHover}
              />
            </Suspense>
          </SafeMount>
        </group>
      )}
      {showLabel && !inCut && !state.onStage && (
        // A FIXED-SIZE nameplate, not a distanceFactor one: scaled with distance
        // it filled the screen when you stood next to someone (the reference
        // note on WorldLabel's width/scale traps).
        <WorldLabel position={[0, height + 0.32, 0]} center occlude={false} style={{ pointerEvents: 'none' }}>
          <div {...nameProps(onSelect, { ...labelStyle(tier === 'near' && free), font: '600 11.5px system-ui, sans-serif', padding: '3px 9px', transform: 'none', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' })}>{labelText}</div>
        </WorldLabel>
      )}
    </group>
  );
}
